import type {
    LocationQuery,
    RouteLocationNormalized,
    RouteLocationRaw,
    RouteParams,
} from "vue-router";

// Same read as draftIdFromQuery. Kept here so this decision stays free of
// the branch-view composable and the stores it pulls in.
const draftIdFrom = (query: LocationQuery) => {
    const id = query.draft ?? query.thread;
    return typeof id === "string" ? id : undefined;
};

export type DraftCarryRoute = Pick<
    RouteLocationNormalized,
    "name" | "path" | "fullPath" | "hash" | "params" | "query" | "meta"
>;

export type DraftCarry =
    | { action: "allow" }
    | { action: "keep"; to: RouteLocationRaw }
    | { action: "leave" }
    | { action: "confirm" };

const namedParam = (params: RouteParams, key: string) => {
    const value = params[key];
    return typeof value === "string" && value.length > 0 ? value : undefined;
};

const sameParams = (left: RouteParams, right: RouteParams) => {
    const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
    for (const key of keys) {
        const a = left[key];
        const b = right[key];
        if (Array.isArray(a) || Array.isArray(b)) {
            if (JSON.stringify(a) !== JSON.stringify(b)) return false;
        } else if (a !== b) {
            return false;
        }
    }
    return true;
};

const samePlace = (from: DraftCarryRoute, to: DraftCarryRoute) =>
    from.name === to.name && sameParams(from.params, to.params);

// The stek's own pages: Design, Build, Run, and the entities in them.
// outsideMode pages share the stek's URL prefix (settings, personal home)
// but they are not the model the draft is editing.
const staysInsideStek = (from: DraftCarryRoute, to: DraftCarryRoute) => {
    if (to.meta.outsideMode) return false;
    const orgName = namedParam(to.params, "orgName");
    const stekName = namedParam(to.params, "stekName");
    if (!orgName || !stekName) return false;
    return (
        orgName === namedParam(from.params, "orgName") &&
        stekName === namedParam(from.params, "stekName")
    );
};

const withDraft = (to: DraftCarryRoute, draftId: string): RouteLocationRaw => {
    const { thread: _legacyThread, ...query } = to.query;
    const next: LocationQuery = { ...query, draft: draftId };
    if (to.name) {
        return {
            name: to.name,
            params: to.params,
            query: next,
            hash: to.hash,
        };
    }
    return { path: to.path, query: next, hash: to.hash };
};

/**
 * What a navigation that would drop the open draft should do.
 *
 * Inside the same stek the draft stays: a link that forgot `?draft=` is sent
 * on with it. The same page with the query cleared is Leave draft. Anywhere
 * else the caller asks first. Nothing is remembered, so a later return does
 * not reopen the draft.
 *
 * A different draft id on the destination is a switch, not a drop. The
 * current draft is left (the existing cleanup) and the new id goes through.
 */
export const decideDraftCarry = (
    from: DraftCarryRoute,
    to: DraftCarryRoute
): DraftCarry => {
    const fromId = draftIdFrom(from.query);
    const toId = draftIdFrom(to.query);
    if (!fromId || toId === fromId) return { action: "allow" };
    if (toId) return { action: "leave" };
    if (staysInsideStek(from, to) && !samePlace(from, to)) {
        return { action: "keep", to: withDraft(to, fromId) };
    }
    if (samePlace(from, to)) return { action: "leave" };
    return { action: "confirm" };
};

export type AcceptedDraftLeave = { path: string | null };

export type DraftCarryHooks = {
    prepareDraftExit: () => Promise<void>;
    confirmLeave: (onAccept: () => void) => void;
    push: (to: RouteLocationRaw) => void;
};

const pushable = (to: DraftCarryRoute): RouteLocationRaw => {
    const location = {
        params: to.params,
        query: to.query,
        hash: to.hash,
    };
    if (to.name) return { ...location, name: to.name };
    return { ...location, path: to.path };
};

/**
 * Applies {@link decideDraftCarry} for a navigation guard.
 *
 * A keep redirects before any leave, so an empty auto-created draft is not
 * deleted on the way to another page of the same stek. A confirm cancels
 * the navigation; accepting it repeats that exact target once, and only
 * that repeat runs the leave.
 */
export const applyDraftCarry = async (
    to: DraftCarryRoute,
    from: DraftCarryRoute,
    viewing: boolean,
    hooks: DraftCarryHooks,
    accepted: AcceptedDraftLeave
): Promise<false | RouteLocationRaw | void> => {
    if (!viewing) return;

    const decision = decideDraftCarry(from, to);
    if (decision.action === "allow") return;
    if (decision.action === "keep") return decision.to;
    if (decision.action === "leave") {
        await hooks.prepareDraftExit();
        return;
    }

    if (accepted.path === to.fullPath) {
        accepted.path = null;
        await hooks.prepareDraftExit();
        return;
    }

    hooks.confirmLeave(() => {
        accepted.path = to.fullPath;
        hooks.push(pushable(to));
    });
    return false;
};
